import apiClient from './apiClient';

export interface Notification {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  read: boolean;
  createdAt: string; // Backend returns string timestamp
  created_at?: string; // Alternative field name for compatibility
  expiresAt?: string;
}

export interface NotificationPreferences {
  email_meetup_invites: boolean;
  email_meetup_updates: boolean;
  email_meetup_reminders: boolean;
  push_meetup_invites: boolean;
  push_meetup_updates: boolean;
  push_meetup_reminders: boolean;
  reminder_hours_before: number;
}

export interface DNDSettings {
  dnd_start_time: string | null;
  dnd_end_time: string | null;
}

export interface DigestSettings {
  digest_enabled: boolean;
  digest_frequency: 'daily' | 'weekly';
}

export interface NotificationsResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const notificationApi = {
  // Get user notifications
  getNotifications: async (page: number = 1, limit: number = 20, unreadOnly: boolean = false): Promise<NotificationsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(unreadOnly && { unread: 'true' })
    });
    
    const response = await apiClient.api.get(`/v1/notifications?${params}`);
    return response.data.data;
  },

  // Get unread notification count
  getUnreadCount: async (): Promise<number> => {
    const response = await apiClient.api.get('/v1/notifications/unread-count');
    return response.data.data.count;
  },

  // Mark notification as read
  markAsRead: async (notificationId: number): Promise<void> => {
    await apiClient.api.put(`/v1/notifications/${notificationId}/read`);
  },

  // Mark notification as unread
  markAsUnread: async (notificationId: number): Promise<void> => {
    await apiClient.api.put(`/v1/notifications/${notificationId}/unread`);
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<void> => {
    await apiClient.api.put('/v1/notifications/read-all');
  },

  // Delete a notification
  deleteNotification: async (notificationId: number): Promise<void> => {
    await apiClient.api.delete(`/v1/notifications/${notificationId}`);
  },

  // Get notification preferences
  getPreferences: async (): Promise<NotificationPreferences> => {
    const response = await apiClient.api.get('/v1/notifications/preferences');
    return response.data.data;
  },

  // Update notification preferences
  updatePreferences: async (preferences: Partial<NotificationPreferences>): Promise<void> => {
    await apiClient.api.put('/v1/notifications/preferences', preferences);
  },

  // Subscribe to push notifications
  subscribeToPush: async (subscription: PushSubscription): Promise<void> => {
    const subscriptionData = subscription.toJSON();
    await apiClient.api.post('/v1/notifications/subscribe', {
      endpoint: subscriptionData.endpoint,
      keys: {
        p256dh: subscriptionData.keys?.p256dh,
        auth: subscriptionData.keys?.auth
      },
      browser: navigator.userAgent
    });
  },

  // Unsubscribe from push notifications
  unsubscribePush: async (endpoint: string): Promise<void> => {
    await apiClient.api.post('/v1/notifications/unsubscribe', { endpoint });
  },

  // Create test notification (development only)
  createTestNotification: async (type?: string, title?: string, message?: string): Promise<Notification> => {
    const response = await apiClient.api.post('/v1/notifications/test', {
      type: type || 'meetup_update',
      title: title || 'Test Notification',
      message: message || 'This is a test notification'
    });
    return response.data.data;
  },

  // --- Do Not Disturb ---

  getDNDSettings: async (): Promise<DNDSettings> => {
    const response = await apiClient.api.get('/v1/notifications/dnd');
    return response.data.data;
  },

  updateDNDSettings: async (settings: DNDSettings): Promise<void> => {
    await apiClient.api.put('/v1/notifications/dnd', settings);
  },

  // --- Digest ---

  getDigestSettings: async (): Promise<DigestSettings> => {
    const response = await apiClient.api.get('/v1/notifications/digest');
    return response.data.data;
  },

  updateDigestSettings: async (settings: { enabled: boolean; frequency: 'daily' | 'weekly' }): Promise<void> => {
    await apiClient.api.put('/v1/notifications/digest', settings);
  },
};

// Notification type helpers
export const getNotificationIcon = (type: string): string => {
  const icons: Record<string, string> = {
    meetup_invite: '📬',
    meetup_invitation: '📬',
    meetup_update: '📝',
    meetup_reminder: '⏰',
    participant_joined: '👋',
    participant_left: '👋',
    meetup_cancelled: '❌',
    location_shared: '📍',
    meetup_location_updated: '📍',
    meetup_location_selected: '📍',
    meetup_location_cleared: '📍',
    suggestion_added: '💡',
    vote_cast: '🗳️',
    group_invitation: '👥',
    guest_response: '🤝',
    guest_account_created: '✨'
  };
  return icons[type] || '📢';
};

export const getNotificationColor = (type: string): string => {
  const colors: Record<string, string> = {
    meetup_invite: 'text-violet-600',
    meetup_invitation: 'text-violet-600',
    meetup_update: 'text-blue-600',
    meetup_reminder: 'text-yellow-600',
    participant_joined: 'text-green-600',
    participant_left: 'text-orange-600',
    meetup_cancelled: 'text-red-600',
    location_shared: 'text-indigo-600',
    meetup_location_updated: 'text-indigo-600',
    meetup_location_selected: 'text-indigo-600',
    meetup_location_cleared: 'text-indigo-600',
    suggestion_added: 'text-purple-600',
    vote_cast: 'text-teal-600',
    group_invitation: 'text-blue-600',
    guest_response: 'text-green-600',
    guest_account_created: 'text-emerald-600'
  };
  return colors[type] || 'text-gray-600';
};

export const formatNotificationTime = (date: Date | string | null | undefined): string => {
  try {
    // Handle null/undefined
    if (!date) {
      console.warn('No date provided to formatNotificationTime');
      return 'Unknown time';
    }

    const now = new Date();
    let notificationDate: Date;

    if (date instanceof Date) {
      notificationDate = date;
    } else if (typeof date === 'string') {
      // Handle various string formats
      if (date.includes('T') || date.includes('Z')) {
        // ISO string format
        notificationDate = new Date(date);
      } else if (date.match(/^\d+$/)) {
        // Unix timestamp
        notificationDate = new Date(parseInt(date));
      } else {
        // Try parsing as regular date string
        notificationDate = new Date(date);
      }
    } else {
      console.warn('Unexpected date format in formatNotificationTime:', typeof date, date);
      return 'Unknown time';
    }

    // Check if date is valid
    if (isNaN(notificationDate.getTime())) {
      console.warn('Invalid date created in formatNotificationTime. Original:', date, 'Parsed:', notificationDate);
      return 'Unknown time';
    }

    const diff = now.getTime() - notificationDate.getTime();

    // Handle future dates (clock skew, but allow small differences)
    if (diff < -60000) { // More than 1 minute in the future
      console.warn('Notification date is in the future:', date, 'Diff:', diff);
      return 'Just now';
    }

    const minutes = Math.floor(Math.abs(diff) / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 4) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;

    return notificationDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: notificationDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  } catch (error) {
    console.error('Error formatting notification time:', error, 'Date:', date);
    return 'Unknown time';
  }
};
